<template>
    <section class="doc-section">

        <div class="mb-6">
            <BotonesNavegacion @cambio-plataforma="plataformaActual = $event" />
        </div>

        <header class="header-container">
            <h1>Geocercas</h1>
            <p class="intro-text">
                Una geocerca es una delimitación geográfica virtual hecha a través del sistema, en la que
                se puede trazar un círculo que detecta las acciones de los dispositivos al entrar y/o al
                salir. Esto permite administrar con precisión si el dispositivo se encuentra fuera o dentro
                del área.
            </p>
            <p class="instruction-text">
                Para visualizar la lista de geocercas creadas en el sistema, vaya al menú y haga clic sobre
                la opción <strong>Geocercas</strong>. Se mostrará la pantalla principal con el listado de
                los registros existentes.
            </p>
        </header>

        <div class="content-layout">

            <!-- Columna de Texto Numerado -->
            <div class="text-column">
                <ol class="config-list">
                    <li>
                        <strong>Campo Buscar:</strong> Permite filtrar los elementos de la tabla en
                        tiempo real para localizar registros específicos.
                    </li>
                    <li>
                        <strong>Botón Agregar geocercas:</strong> Permite acceder a la vista de creación
                        para registrar una nueva delimitación geográfica.
                    </li>
                    <li>
                        <strong>Tabla de registros:</strong> Visualización de las geocercas que incluye
                        el nombre, tipo de alerta (entrada, salida o ambos), el usuario creador,
                        dispositivos asignados y acciones de edición o eliminación.
                    </li>
                    <li>
                        <strong>Paginación:</strong> Control de navegación para visualizar el resto de
                        los registros en caso de que la lista sea extensa.
                    </li>
                </ol>
            </div>

            <!-- Columna de Carrusel Dinámico -->
            <div class="image-column">
                <!-- Usamos una key para forzar el refresco de la animación al cambiar de plataforma -->
                <transition name="fade" mode="out-in">
                    <CarruselAciqui 
                        :key="plataformaActual" 
                        v-if="imagenesAMostrar.length > 0" 
                        :items="imagenesAMostrar" 
                    />
                </transition>
            </div>

        </div>

        <!-- Nota informativa -->
        <footer class="footer-note note-info">
            <svg class="note-icon" width="15" height="15" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                <path d="M8 7.5V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                <circle cx="8" cy="5.25" r="0.75" fill="currentColor"/>
            </svg>
            <p>
                El <strong>administrador</strong> podrá visualizar todas las geocercas registradas por
                los usuarios, mientras que el <strong>usuario final</strong> únicamente podrá visualizar
                las geocercas que él mismo ha registrado.
            </p>
        </footer>

    </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import CarruselAciqui from '../Carruselimg.vue'
import BotonesNavegacion from '../BotonesNavegacion.vue'

const plataformaActual = ref('web')

const imagenesWeb = [
    {
        nombre: "Listado de Geocercas (Web)",
        url: "img/adminGeocercas/GeocercasAdmin.png",
        descripcion: "Interfaz del listado principal de Geocercas en escritorio."
    }
]

const imagenesMobile = [
    {
        nombre: "Listado de Geocercas (Móvil)",
        url: "img/adminGeocercas/ListaGeocercas_Movil.png",
        descripcion: "Interfaz del listado principal de Geocercas en escritorio."
    }
]

const imagenesAMostrar = computed(() => {
    return plataformaActual.value === 'web' ? imagenesWeb : imagenesMobile
})
</script>

<style scoped>
/* Añadimos un pequeño margen para separar los botones del título */
.mb-6 {
    margin-bottom: 2rem;
}

/* Reutilizamos tu animación fade para el cambio de imágenes */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}

/* ── Textos de introducción ───────────────────────── */
.intro-text,
.instruction-text {
    font-size: 15px;
    opacity: 0.8;
    margin-bottom: 12px;
}

.instruction-text {
    margin-bottom: 30px;
}

/* ── Layout ───────────────────────────────────────── */
.content-layout {
    display: flex;
    gap: 40px;
    align-items: flex-start;
}

/* ── Lista ────────────────────────────────────────── */
.text-column {
    flex: 1;
}

.config-list {
    padding-left: 20px;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.config-list li {
    font-size: 14px;
    padding: 12px 14px;
    background: var(--code-bg);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    line-height: 1.6;
}

/* ── Carrusel ─────────────────────────────────────── */
.image-column {
    flex: 1;
    position: sticky;
    top: 20px;
    min-height: 200px; /* Reserva espacio mientras carga */
}

/* ── Sistema de Notas ─────────────────────────────── */
.footer-note {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 30px;
    padding: 13px 16px;
    border-radius: 6px;
    border: 1px solid;
    font-size: 14px;
}

.footer-note p {
    margin: 0;
    line-height: 1.6;
}

.note-icon {
    flex-shrink: 0;
    margin-top: 1px;
}

/* Informativa — azul */
.note-info {
    background: var(--code-bg);
    border-color: var(--primary-color);
    color: var(--primary-color);
}
.note-info p      { color: var(--text-color); }
.note-info strong { color: var(--text-color); font-weight: 700; }

/* Advertencia — ámbar */
.note-warning {
    background: var(--code-bg);
    border-color: #d97706;
    color: #d97706;
}
.note-warning p      { color: var(--text-color); }
.note-warning strong { color: var(--text-color); font-weight: 700; }

/* Tip / Consejo — verde */
.note-tip {
    background: var(--code-bg);
    border-color: #16a34a;
    color: #16a34a;
}
.note-tip p      { color: var(--text-color); }
.note-tip strong { color: var(--text-color); font-weight: 700; }

/* ── Responsive ───────────────────────────────────── */
@media (max-width: 1024px) {
    .content-layout {
        flex-direction: column;
    }

    .image-column {
        position: static;
        width: 100%;
    }
}
</style>